import { Empty } from '@byecode/ui'
import { DndContext, MeasuringStrategy } from '@dnd-kit/core'
import { arrayMove, horizontalListSortingStrategy, SortableContext } from '@dnd-kit/sortable'
import { getAssetUrl } from '@lighthouse/assets'
import type {
    ActorEvent,
    ButtonItem,
    FieldADTValue,
    FieldValue,
    KanbanColumnsSort,
    KanbanViewConfig,
    RecordLikeProtocol,
    RichTextContentProtocol,
    TableColumns,
    ViewField
} from '@lighthouse/core'
import {
    getGroupAllVisibleOptions,
    getGroupVisibleWithLabelOptions,
    getPrimaryDataSourceEnableFieldIds,
    getRecordPoolKey,
    getViewColumns,
    mergeGroupVisibleOptions,
    useApplicationContext,
    useAtomData,
    useLanguageContext,
    ViewEmptyState
} from '@lighthouse/shared'
import { find } from 'rambda'
import type { FC } from 'react'
import React, { useCallback, useMemo } from 'react'
import { useImmer } from 'use-immer'

import { useEffectiveAnchorId, useViewBlockContext } from '../../context'
import { useScrollToAnchorWhenHashMatch } from '../../hooks/useScrollToAnchorWhenHashMatch'
import { DragOverlay } from './DragOverlay'
import { DroppableContainer } from './DroppableContainer'
import { useDndSortable, useGroupRecordByField } from './hooks'
import * as SC from './styles'

interface Props {
    tablePropsCache?: TableColumns
    blockWidth?: number
    cachedKanbanSort?: KanbanColumnsSort
    onChangeDisplay?: (data?: TableColumns) => void
    onCellChange?: (recordId: string, fieldValue: FieldADTValue) => void
    onRecordOperatorActionTrigger?: (id: string, actorEvent: ActorEvent, record?: RecordLikeProtocol) => Promise<void>
    onRecordClick?: (id: string, actorEvent: ActorEvent, record?: RecordLikeProtocol) => Promise<void>
    onRenderRichText: (v: RichTextContentProtocol, record?: RecordLikeProtocol) => string
    onChangeCachedKanbanSort: (val: KanbanColumnsSort | undefined) => void
    handleClickAdd?: (content?: Record<string, FieldValue>) => Promise<RecordLikeProtocol | undefined>
}

export const KanbanBoardBlock: FC<Props> = ({
    tablePropsCache,
    blockWidth,
    cachedKanbanSort,
    onCellChange,
    onRecordOperatorActionTrigger,
    onRecordClick,
    onRenderRichText,
    onChangeCachedKanbanSort,
    handleClickAdd
}) => {
    const { personOptions, isMobile } = useApplicationContext()
    const { blockData, dataSource, dataSourceList, recordPoolAtom, recordPoints } = useViewBlockContext()
    const { config, id } = blockData
    const {
        canCreateRecord,
        canSetKanban,
        canSortKanban,
        canDisplay,
        kanbanGroupConfigure = { groupByFieldId: '', groupConfig: [] },
        pointer,
        viewFieldSettings,
        anchor
    } = config as KanbanViewConfig
    const { anchorId } = anchor || {}
    const effectiveAnchorId = useEffectiveAnchorId(anchorId)
    useScrollToAnchorWhenHashMatch(effectiveAnchorId, anchor)
    const { breakPoint: baseBreakPointConfig } = config as KanbanViewConfig
    const { emptyView } = baseBreakPointConfig || {}
    const { groupByFieldId: kanbanGroupByField, groupConfig: kanbanGroupConfig } = kanbanGroupConfigure
    const { id: dsId, schema, viewOptions, type } = dataSource
    const { tableProps } = viewOptions || {}
    const noData = !pointer
    const { convertTextByLanguage } = useLanguageContext()
    // block 设置后的tableProps
    const settingColumns = useMemo(
        () =>
            getViewColumns({
                blockId: id,
                tableProps,
                value: viewFieldSettings,
                schema
            }),
        [id, schema, tableProps, viewFieldSettings]
    )

    const records = useAtomData(
        recordPoolAtom,
        useCallback(
            pool => recordPoints.map(p => pool.get(getRecordPoolKey(dsId, p.id))).filter((r): r is RecordLikeProtocol => r !== undefined),
            [dsId, recordPoints]
        )
    )

    // 用户字段设置后的tableProps
    const columns = useMemo(() => {
        const filterColumns = settingColumns.filter(item => item.visible)
        if (!tablePropsCache || tablePropsCache.length === 0 || !canDisplay) {
            return filterColumns
        }
        const columnsCache = tablePropsCache.reduce<ViewField[]>((prev, cur) => {
            const field = find(({ fieldId }) => fieldId === cur.id, filterColumns)
            if (field) {
                prev.push({
                    ...field,
                    visible: cur.visible
                })
            }
            return prev
        }, [])
        return filterColumns.reduce<ViewField[]>((prev, cur) => {
            if (!cur.visible) {
                return prev
            }
            const field = find(({ fieldId }) => fieldId === cur.fieldId, prev)
            if (!field) {
                prev.push({
                    ...cur,
                    visible: false
                })
            }
            return prev
        }, columnsCache)
    }, [settingColumns, tablePropsCache, canDisplay])

    const groupedFieldSchema = useMemo(() => (kanbanGroupByField ? schema[kanbanGroupByField] : undefined), [kanbanGroupByField, schema])

    // 合并校验后的选项配置
    const mergedSortRule = useMemo(() => {
        if (!kanbanGroupByField) {
            return
        }

        const currentViewCache = cachedKanbanSort?.[kanbanGroupByField] ?? []

        if (!kanbanGroupConfig && (!currentViewCache || currentViewCache.length === 0)) {
            return
        }

        const field = schema[kanbanGroupByField]
        if (!field) {
            return
        }
        const completeCurrentViewCache = getGroupVisibleWithLabelOptions(field, currentViewCache, { personOptions })
        if (!kanbanGroupConfig) {
            return completeCurrentViewCache
        }

        const completeKanbanVisibleConfig = mergeGroupVisibleOptions(
            getGroupVisibleWithLabelOptions(field, kanbanGroupConfig, { personOptions }),
            getGroupAllVisibleOptions(field, { personOptions })
        )

        if (completeCurrentViewCache.length === 0) {
            return completeKanbanVisibleConfig
        }

        return mergeGroupVisibleOptions(completeCurrentViewCache, completeKanbanVisibleConfig)
    }, [kanbanGroupByField, cachedKanbanSort, kanbanGroupConfig, schema, personOptions])

    const handleColumnSortChange = useCallback(
        (before: string, after: string) => {
            if (!mergedSortRule || !kanbanGroupByField) {
                return
            }

            const beforeIndex = mergedSortRule.findIndex(item => item.value === before)
            const afterIndex = mergedSortRule.findIndex(item => item.value === after)

            onChangeCachedKanbanSort({
                [kanbanGroupByField]: arrayMove(mergedSortRule, beforeIndex, afterIndex).map(item => ({
                    value: item.value,
                    visible: item.visible
                }))
            })
        },
        [kanbanGroupByField, mergedSortRule, onChangeCachedKanbanSort]
    )

    // 根据分组字段进行分组
    const groupList = useGroupRecordByField(kanbanGroupByField, schema, {
        records: records || [],
        visibleConfig: mergedSortRule,
        personOptions
    })

    // 拖拽数据及交互逻辑
    const { draggingData, activeId, items, containers, sensors, collisionDetection, onDragStart, onDragCancel, onDragEnd, onDragOver } =
        useDndSortable(
            groupList,
            groupedFieldSchema,
            useMemo(() => {
                if (!mergedSortRule) {
                    return undefined
                }
                if (mergedSortRule.length === 0) {
                    return undefined
                }
                return mergedSortRule.filter(item => item.visible).map(item => item.value)
            }, [mergedSortRule]),
            onCellChange,
            handleColumnSortChange
        )

    const groupedFieldValue = useMemo(() => [activeId], [activeId])

    // ##################################################################### //
    // ########################### 看板列分页状态 ########################### //
    // ##################################################################### //
    const [colPaging, setColPaging] = useImmer({ page: 1, pageSize: 10 })

    const pagingContainers = useMemo(() => {
        return containers.slice(0, colPaging.page * colPaging.pageSize)
    }, [colPaging.page, colPaging.pageSize, containers])

    const nextColPagingLoadNum = Math.min(10, containers.length - pagingContainers.length)

    const handlePagingNext = useCallback(() => {
        setColPaging(draft => {
            draft.page++
        })
    }, [setColPaging])

    // ##################################################################### //
    // ########################### 看板列显示状态 ########################### //
    // ##################################################################### //
    const [collapsedContainers, setCollapsedContainers] = useImmer<Record<string, boolean>>({})

    const handleCollapse = useCallback(
        (id: string) => {
            setCollapsedContainers(draft => void (draft[id] = !draft[id]))
        },
        [setCollapsedContainers]
    )

    const handleHidden = useCallback(
        (v: string) => {
            if (!kanbanGroupByField) {
                onChangeCachedKanbanSort(undefined)
                return
            }
            const cacheViewConfig = cachedKanbanSort?.[kanbanGroupByField]
            if (cacheViewConfig) {
                const newCacheViewConfig = cacheViewConfig.map(item => {
                    if (item.value === v) {
                        return {
                            ...item,
                            visible: false
                        }
                    }
                    return item
                })
                onChangeCachedKanbanSort({
                    [kanbanGroupByField]: newCacheViewConfig
                })
                return
            }
            if (mergedSortRule) {
                const initCachedKanbanSort = {
                    [kanbanGroupByField]: mergedSortRule.map(item => {
                        if (item.value === v) {
                            return { value: item.value, visible: false }
                        }
                        return { value: item.value, visible: item.visible }
                    })
                }
                onChangeCachedKanbanSort(initCachedKanbanSort)
            }
        },
        [cachedKanbanSort, kanbanGroupByField, mergedSortRule, onChangeCachedKanbanSort]
    )

    const primaryDataSourceFieldIds = useMemo(() => {
        if (!schema || !viewOptions || !type) {
            return
        }
        return getPrimaryDataSourceEnableFieldIds(schema, viewOptions, type)
    }, [schema, viewOptions, type])

    return useMemo(() => {
        if (noData) {
            return (
                <ViewEmptyState
                    id={effectiveAnchorId}
                    config={emptyView}
                    onRenderRichText={onRenderRichText}
                    rootStyle={{ padding: '16px 0' }}
                />
            )
        }

        if (!kanbanGroupByField || !schema?.[kanbanGroupByField] || !primaryDataSourceFieldIds?.has(kanbanGroupByField)) {
            return (
                <Empty
                    id={effectiveAnchorId}
                    styles={{ root: { padding: '16px 0' } }}
                    icon={<img style={{ width: 140, height: 144 }} src={getAssetUrl('empty', 'no_kanban_group.svg')} alt="没有分组依据" />}
                    description={convertTextByLanguage('pleasesSetKanbanGrouping')}
                />
            )
        }

        return (
            <SC.KanbanBoardRoot id={effectiveAnchorId}>
                <DndContext
                    sensors={sensors}
                    // collisionDetection={collisionDetection}
                    measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
                    onDragStart={onDragStart}
                    // onDragOver={onDragOver}
                    onDragEnd={onDragEnd}
                    onDragCancel={onDragCancel}
                >
                    <SortableContext
                        disabled={!canSortKanban || isMobile}
                        items={pagingContainers}
                        strategy={horizontalListSortingStrategy}
                    >
                        {pagingContainers.map(containerId => {
                            return (
                                <DroppableContainer
                                    blockId={id}
                                    key={containerId}
                                    id={containerId}
                                    disabled={!canSortKanban || isMobile}
                                    enableHidden={canSetKanban}
                                    list={items[containerId] ?? []}
                                    schema={schema}
                                    columns={columns}
                                    groupedFieldValue={[containerId]}
                                    fieldSchema={schema[kanbanGroupByField]}
                                    collapsed={collapsedContainers[containerId]}
                                    creatable={canCreateRecord}
                                    handleCollapse={() => handleCollapse(containerId)}
                                    handleHidden={() => handleHidden(containerId)}
                                    onRecordOperatorActionTrigger={onRecordOperatorActionTrigger}
                                    onRecordClick={onRecordClick}
                                    onRenderButtonTitle={onRenderRichText}
                                    handleClickAdd={handleClickAdd}
                                />
                            )
                        })}

                        {nextColPagingLoadNum !== 0 && (
                            <SC.KanbanBoardLoadMoreBtn onClick={handlePagingNext}>
                                加载更多 {nextColPagingLoadNum}
                            </SC.KanbanBoardLoadMoreBtn>
                        )}
                    </SortableContext>

                    {groupedFieldSchema && (
                        <DragOverlay
                            blockId={id}
                            draggingData={draggingData}
                            groupedFieldValue={groupedFieldValue}
                            schema={schema}
                            groupedFieldSchema={groupedFieldSchema}
                            columns={columns}
                            enableHidden={canSetKanban}
                            collapsed={activeId ? collapsedContainers[activeId] : false}
                            onRenderButtonTitle={onRenderRichText}
                        />
                    )}
                </DndContext>
            </SC.KanbanBoardRoot>
        )
    }, [
        noData,
        effectiveAnchorId,
        kanbanGroupByField,
        schema,
        primaryDataSourceFieldIds,
        sensors,
        onDragStart,
        onDragEnd,
        onDragCancel,
        canSortKanban,
        isMobile,
        pagingContainers,
        nextColPagingLoadNum,
        handlePagingNext,
        groupedFieldSchema,
        id,
        draggingData,
        groupedFieldValue,
        columns,
        canSetKanban,
        activeId,
        collapsedContainers,
        onRenderRichText,
        emptyView,
        convertTextByLanguage,
        items,
        canCreateRecord,
        onRecordOperatorActionTrigger,
        onRecordClick,
        handleClickAdd,
        handleCollapse,
        handleHidden
    ])
}
